import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useApps } from '@/contexts/AppsContext';
import { Toaster } from 'sonner';

// Contexts
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AppsProvider } from '@/contexts/AppsContext';
import { MindWebSocketProvider } from '@/contexts/MindWebSocketContext';

// Layouts
import DashboardLayout from '@/components/layout/DashboardLayout';

// Auth Pages
import LoginPage from '@/pages/auth/LoginPage';
import SignupPage from '@/pages/auth/SignupPage';
import AuthCallback from '@/pages/auth/AuthCallback';
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';

// Dashboard Pages
import DashboardPage from '@/pages/dashboard/DashboardPage';

// Apps Pages
import AppsListPage from '@/pages/apps/AppsListPage';
import CreateAppWizard from '@/pages/apps/CreateAppWizard';
import AppDetailPage from '@/pages/apps/AppDetailPage';
import SchemaPage from '@/pages/apps/SchemaPage';

// Keys Pages
import KeysListPage from '@/pages/keys/KeysListPage';
import CreateKeyPage from '@/pages/keys/CreateKeyPage';

// Billing Pages
import BillingPage from '@/pages/billing/BillingPage';

// Settings Pages
import SettingsPage from '@/pages/settings/SettingsPage';

// Mind Pages
import MindPage from '@/pages/mind/MindPage';

// Data Pages
import DataPage from '@/pages/data/DataPage';

// Docs Pages
import DocumentationPage from '@/pages/docs/DocumentationPage';

// Legal Pages
import TermsPage from '@/pages/legal/TermsPage';
import PrivacyPage from '@/pages/legal/PrivacyPage';

// Admin Pages
import AdminOverviewPage from '@/pages/admin/AdminOverviewPage';
import AdminTenantsPage from '@/pages/admin/AdminTenantsPage';
import IngestPage from '@/pages/admin/IngestPage';
import ServiceCatalogPage from '@/pages/admin/ServiceCatalogPage';

// Route Guards
import { AdminRoute, OwnerRoute } from '@/components/auth/RouteGuards';

// Smart redirect: admins go to /admin, everyone else to /dashboard
function SmartRedirect() {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />;
}

// Redirect old flat routes to app-scoped routes
function AppRedirect({ to }) {
  const { currentApp } = useApps();
  if (!currentApp) return <Navigate to="/dashboard" replace />;
  return <Navigate to={`/apps/${currentApp.appId}/${to}`} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider defaultTheme="dark">
        <AuthProvider>
          <AppsProvider>
          <MindWebSocketProvider>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* Public pages — accessible without auth */}
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/documentation" element={<DocumentationPage isPublic />} />

              {/* Protected Routes */}
              <Route element={<DashboardLayout />}>
                {/* Global routes — no app scope */}
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/apps" element={<AppsListPage />} />
                <Route path="/apps/new" element={<CreateAppWizard />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/:tab" element={<SettingsPage />} />

                {/* App-scoped routes — appId in URL */}
                <Route path="/apps/:appId" element={<OwnerRoute><AppDetailPage /></OwnerRoute>} />
                <Route path="/apps/:appId/mind" element={<OwnerRoute><MindPage /></OwnerRoute>} />
                <Route path="/apps/:appId/data" element={<OwnerRoute><DataPage /></OwnerRoute>} />
                <Route path="/apps/:appId/keys" element={<OwnerRoute><KeysListPage /></OwnerRoute>} />
                <Route path="/apps/:appId/keys/new" element={<OwnerRoute><CreateKeyPage /></OwnerRoute>} />
                <Route path="/apps/:appId/schema" element={<OwnerRoute><SchemaPage /></OwnerRoute>} />
                <Route path="/apps/:appId/billing" element={<OwnerRoute><BillingPage /></OwnerRoute>} />
                <Route path="/apps/:appId/ingest" element={<AdminRoute><IngestPage /></AdminRoute>} />

                {/* Admin — guarded */}
                <Route path="/admin" element={<AdminRoute><AdminOverviewPage /></AdminRoute>} />
                <Route path="/admin/tenants" element={<AdminRoute><AdminTenantsPage /></AdminRoute>} />
                <Route path="/admin/services" element={<AdminRoute><ServiceCatalogPage /></AdminRoute>} />

                {/* Legacy flat route redirects */}
                <Route path="/mind" element={<AppRedirect to="mind" />} />
                <Route path="/data" element={<AppRedirect to="data" />} />
                <Route path="/keys" element={<AppRedirect to="keys" />} />
                <Route path="/keys/new" element={<AppRedirect to="keys/new" />} />
                <Route path="/billing" element={<AppRedirect to="billing" />} />
                <Route path="/ingest" element={<AppRedirect to="ingest" />} />
              </Route>

              {/* Redirects — admin → /admin, tenant owner → /dashboard */}
              <Route path="/" element={<SmartRedirect />} />
              <Route path="*" element={<SmartRedirect />} />
            </Routes>

            {/* Global Toast Notifications */}
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: 'hsl(var(--card) / 0.9)',
                  backdropFilter: 'blur(24px)',
                  border: '1px solid hsl(var(--glass-border))',
                  color: 'hsl(var(--foreground))',
                  boxShadow: '0 0 20px hsl(var(--glow) / 0.15)',
                },
              }}
            />
          </MindWebSocketProvider>
          </AppsProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
